/**
 * Destinataires en copie (Cc) d'un fil de messages — logique pure.
 *
 * COPIE CONFORME : apps/web/src/lib/ccRecipients.ts (le web et l'API sont compilés
 * séparément). Les deux fichiers doivent rester identiques ; le test
 * apps/web/src/lib/ccRecipients.test.ts exécute les mêmes cas sur les deux.
 *
 * Modèle (par dossier) :
 * - `threads.participants` (jsonb, tableau de chaînes) : destinataires du DERNIER e-mail client
 *   (reply-all). /api/mail/inbound REMPLACE la liste à chaque e-mail reçu ; c'est la seule
 *   écriture de cette colonne (hors fils créés avant la migration) ;
 * - `threads.manual_participants` (jsonb) : adresses ajoutées à la main (bandeau Cc ou copies
 *   choisies avant le premier envoi). Elles valent pour toute la suite du dossier ;
 * - `threads.excluded_participants` (jsonb) : adresses retirées à la main. Elles ne sont plus
 *   mises en copie et ne sont jamais réactivées automatiquement ; seul un ajout manuel les réactive ;
 * - le client principal (`cases.client_email`) reste toujours en « À » et jamais en Cc.
 * Copies actives = (participants ∪ manual_participants de tous les fils) − exclus − client.
 * Même règle de validation que la fonction SQL public.normalize_cc_email.
 */

/** Limite des ajouts manuels de copies sur un dossier (contrôlée aussi par la RPC SQL). */
export const MAX_CC_RECIPIENTS = 20;
/** Plafond des copies d'un envoi : Resend refuse plus de 50 destinataires (À + Cc). */
export const MAX_SEND_CC_RECIPIENTS = 45;
export const MAX_EMAIL_LENGTH = 254;

const EMAIL_PATTERN = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:".]{2,}$/;

/** Extrait l'adresse d'une valeur « Nom <adresse> » ou « adresse », en minuscules. */
export function extractEmailAddress(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const value = raw.trim();
  const bracket = value.match(/<([^<>]+)>\s*$/);
  return (bracket?.[1] ?? value).trim().toLowerCase();
}

/** Adresse normalisée (minuscules, sans espaces) si elle est valide, sinon null. */
export function normalizeEmail(raw: unknown): string | null {
  const email = extractEmailAddress(raw);
  if (!email || email.length > MAX_EMAIL_LENGTH || !EMAIL_PATTERN.test(email)) return null;
  return email;
}

export function isValidEmail(raw: unknown): boolean {
  return normalizeEmail(raw) !== null;
}

/** Liste normalisée, dédoublonnée sans tenir compte de la casse, ordre d'origine conservé. */
export function normalizeEmailList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of list) {
    const email = normalizeEmail(item);
    if (email && !seen.has(email)) {
      seen.add(email);
      out.push(email);
    }
  }
  return out;
}

/**
 * Union de deux listes d'adresses (normalisées, dédoublonnées, ordre conservé).
 * Les adresses de `incoming` présentes dans `excluded` ou écartées par `ignore` ne sont pas ajoutées.
 */
export function mergeParticipants(
  existing: unknown,
  incoming: unknown,
  excluded: unknown = [],
  ignore: (email: string) => boolean = () => false,
): string[] {
  const excludedSet = new Set(normalizeEmailList(excluded));
  const result = normalizeEmailList(existing);
  const seen = new Set(result);
  for (const email of normalizeEmailList(incoming)) {
    if (seen.has(email) || excludedSet.has(email) || ignore(email)) continue;
    seen.add(email);
    result.push(email);
  }
  return result;
}

/**
 * Participants d'un e-mail client reçu (À + Cc + expéditeur) : REMPLACENT ceux du fil (reply-all).
 * Les adresses retirées à la main et nos propres adresses (`ignore`) sont écartées.
 */
export function inboundParticipants(
  incoming: unknown,
  excluded: unknown = [],
  ignore: (email: string) => boolean = () => false,
): string[] {
  return mergeParticipants([], incoming, excluded, ignore);
}

/** Copies d'un fil telles que stockées en base (colonnes jsonb de public.threads). */
export interface ThreadCcColumns {
  participants?: unknown;
  manual_participants?: unknown;
  excluded_participants?: unknown;
}

/** Copies au niveau dossier : union des colonnes de tous ses fils. */
export interface CaseCcLists {
  participants: string[];
  manual: string[];
  excluded: string[];
}

export function caseCcLists(rows: readonly ThreadCcColumns[] | null | undefined): CaseCcLists {
  const lists: CaseCcLists = { participants: [], manual: [], excluded: [] };
  for (const row of rows ?? []) {
    lists.participants = mergeParticipants(lists.participants, row.participants);
    lists.manual = mergeParticipants(lists.manual, row.manual_participants);
    lists.excluded = mergeParticipants(lists.excluded, row.excluded_participants);
  }
  return lists;
}

/**
 * Copies actives d'un dossier : (ajouts manuels ∪ participants) − exclus − client principal.
 * Les ajouts manuels viennent en premier : ils sont gardés en priorité si l'envoi est plafonné.
 */
export function caseActiveCcRecipients(lists: CaseCcLists, primaryEmail: unknown): string[] {
  return activeCcRecipients(mergeParticipants(lists.manual, lists.participants), lists.excluded, primaryEmail);
}

/** Plafonne les copies d'un envoi ; `dropped` liste les adresses coupées. */
export function capCcRecipients(
  list: readonly string[],
  max: number = MAX_SEND_CC_RECIPIENTS,
): { cc: string[]; dropped: string[] } {
  return { cc: list.slice(0, max), dropped: list.slice(max) };
}

/** Copies actives : participants moins les exclus, moins le client principal. */
export function activeCcRecipients(
  participants: unknown,
  excluded: unknown,
  primaryEmail: unknown,
): string[] {
  const primary = extractEmailAddress(primaryEmail);
  const excludedSet = new Set(normalizeEmailList(excluded));
  return normalizeEmailList(participants).filter((email) => email !== primary && !excludedSet.has(email));
}

/** Découpe une saisie (virgules, points-virgules, espaces) en adresses valides et invalides. */
export function parseCcInput(text: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[,;\s]+/)) {
    if (!part.trim()) continue;
    const email = normalizeEmail(part);
    if (email) {
      if (!valid.includes(email)) valid.push(email);
    } else {
      invalid.push(part.trim());
    }
  }
  return { valid, invalid };
}

/** Ajoute une adresse aux copies en attente (premier message, fil pas encore créé). */
export function addPendingCc(pending: readonly string[], raw: unknown, primaryEmail: unknown): string[] {
  const email = normalizeEmail(raw);
  if (!email || email === extractEmailAddress(primaryEmail)) return [...pending];
  return normalizeEmailList([...pending, email]);
}

export function removePendingCc(pending: readonly string[], raw: unknown): string[] {
  const email = extractEmailAddress(raw);
  return pending.filter((item) => extractEmailAddress(item) !== email);
}
